import React, { useEffect, useState } from 'react';
import { Copy, Eye, EyeOff, KeyRound, Plus, Trash2 } from 'lucide-react';
import {
  adminCreateLibrary,
  adminDeleteLibrary,
  adminListLibraries,
  adminUpdateLibrary,
  describeApiError,
} from '../../services/api';
import type { PrivateLibrary } from '../../types';
import { useConfirm } from '../../hooks/useConfirm';

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 63);

const LibrariesManagement: React.FC = () => {
  const [libraries, setLibraries] = useState<PrivateLibrary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const { confirm, confirmDialog } = useConfirm();

  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const [editingSlug, setEditingSlug] = useState<string | undefined>();
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPassword, setEditPassword] = useState('');

  const refresh = () => {
    setLoading(true);
    adminListLibraries()
      .then(setLibraries)
      .catch(e => setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const submitCreate = async () => {
    if (!newSlug || !newName || newPassword.length < 4) return;
    setBusy(true);
    setError(undefined);
    try {
      await adminCreateLibrary({
        slug: newSlug,
        name: newName,
        description: newDescription,
        password: newPassword,
      });
      setCreating(false);
      setNewSlug('');
      setNewName('');
      setNewDescription('');
      setNewPassword('');
      refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (lib: PrivateLibrary) => {
    setEditingSlug(lib.slug);
    setEditName(lib.name);
    setEditDescription(lib.description ?? '');
    setEditPassword('');
  };

  const submitEdit = async () => {
    if (!editingSlug) return;
    setBusy(true);
    setError(undefined);
    try {
      const patch: Parameters<typeof adminUpdateLibrary>[1] = {
        name: editName,
        description: editDescription,
      };
      if (editPassword) patch.password = editPassword;
      await adminUpdateLibrary(editingSlug, patch);
      setEditingSlug(undefined);
      refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug: string, name: string) => {
    const ok = await confirm({
      title: `Delete "${name}"?`,
      body: "Its products aren't deleted, but they'll be left without a collection until you reassign them.",
      confirmLabel: 'Delete collection',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await adminDeleteLibrary(slug);
      refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleVisible = async (lib: PrivateLibrary) => {
    const next = lib.visible === false;
    setBusy(true);
    setError(undefined);
    try {
      await adminUpdateLibrary(lib.slug, { visible: next });
      setLibraries(prev =>
        prev.map(l => (l.slug === lib.slug ? { ...l, visible: next } : l))
      );
    } catch (e: any) {
      setError(describeApiError(e, 'Could not change visibility'));
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = (slug: string) => {
    const url = `${window.location.origin}/private/${slug}`;
    navigator.clipboard.writeText(url).catch(() => undefined);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {confirmDialog}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-semibold">Private libraries</h1>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="bg-primary text-white rounded px-4 py-2 text-sm font-medium inline-flex items-center gap-1"
          >
            <Plus size={14} /> New library
          </button>
        )}
      </div>

      <p className="text-sm text-text-light -mt-3">
        Private libraries are password-gated product collections. Use them for school order forms or
        any other group you don't want in the public shop. Share the URL plus the password with
        your customer.
      </p>

      {error && <div className="text-sm text-red-600">{error}</div>}

      {creating && (
        <div className="bg-white border border-background-dark rounded p-4 space-y-3">
          <div className="text-sm font-medium">New library</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-light mb-1">Name</label>
              <input
                value={newName}
                onChange={e => {
                  setNewName(e.target.value);
                  if (!newSlug) setNewSlug(slugify(e.target.value));
                }}
                placeholder="Lincoln Elementary 2026"
                className="w-full border border-background-dark rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-light mb-1">Slug (URL)</label>
              <input
                value={newSlug}
                onChange={e => setNewSlug(slugify(e.target.value))}
                placeholder="lincoln-elementary-2026"
                className="w-full border border-background-dark rounded px-3 py-2 text-sm font-mono"
              />
              <div className="text-xs text-text-light mt-1">
                /private/<span className="font-mono">{newSlug || 'your-slug'}</span>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs text-text-light mb-1">Description (optional)</label>
            <textarea
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              rows={2}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-text-light mb-1">Password (min 4 chars)</label>
            <input
              type="text"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm font-mono"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="border border-background-dark rounded px-3 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitCreate}
              disabled={busy || !newSlug || !newName || newPassword.length < 4}
              className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </div>
      )}

      {loading && <div className="text-text-light text-sm">Loading…</div>}

      {!loading && libraries.length === 0 && !creating && (
        <div className="text-text-light text-sm">No libraries yet.</div>
      )}

      {libraries.map(lib => {
        const editing = editingSlug === lib.slug;
        return (
          <div key={lib.slug} className="bg-white border border-background-dark rounded p-4 space-y-3">
            {editing ? (
              <>
                <div className="text-sm font-medium">
                  Edit <span className="font-mono">{lib.slug}</span>
                </div>
                <div>
                  <label className="block text-xs text-text-light mb-1">Name</label>
                  <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="w-full border border-background-dark rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-light mb-1">Description</label>
                  <textarea
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    rows={2}
                    className="w-full border border-background-dark rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-light mb-1">
                    New password (blank = keep current)
                  </label>
                  <input
                    type="text"
                    value={editPassword}
                    onChange={e => setEditPassword(e.target.value)}
                    className="w-full border border-background-dark rounded px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingSlug(undefined)}
                    className="border border-background-dark rounded px-3 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitEdit}
                    disabled={busy || !editName || (editPassword !== '' && editPassword.length < 4)}
                    className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{lib.name}</span>
                    {lib.visible === false && (
                      <span className="text-xs rounded-full bg-background-dark text-text-light px-2 py-0.5">
                        Hidden
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text-light font-mono">/private/{lib.slug}</div>
                  {lib.description && <div className="text-sm mt-1">{lib.description}</div>}
                  {/* Says what hidden actually means, since "hidden" could
                      reasonably be read as deleted or merely unlisted. */}
                  {lib.visible === false && (
                    <p className="text-xs text-text-light mt-1">
                      Off the site entirely — not in the menu, and the link
                      and password won't open it. Products and settings are
                      untouched.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => toggleVisible(lib)}
                    disabled={busy}
                    className="text-text-light hover:bg-background-dark rounded p-2 disabled:opacity-40"
                    title={lib.visible === false ? 'Make available' : 'Hide from the site'}
                    aria-label={lib.visible === false ? 'Make available' : 'Hide from the site'}
                  >
                    {lib.visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => copyUrl(lib.slug)}
                    className="text-text-light hover:bg-background-dark rounded p-2"
                    title="Copy URL"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(lib)}
                    className="text-text-light hover:bg-background-dark rounded p-2"
                    title="Edit"
                  >
                    <KeyRound size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(lib.slug, lib.name)}
                    className="text-red-600 hover:bg-red-50 rounded p-2"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LibrariesManagement;
